import { useEffect, useMemo, useRef, useState } from "react";
import type { TeamLogMessage, Decision } from "../types";
import type { HelpMessage } from "./HelpPanel";
import { MarkerDot } from "./MarkerDot";
import type { Marker } from "./MarkerDot";

const MARKERS_TARGET = 20;

// Shared hook: re-samples from current scroll position (debounced 150ms) when scrolled.
// Returns the index of the first item in view, resetting to 0 when totalItems changes.
function useScrollReset(scrollRef: { current: Element | null } | undefined, totalItems: number): number {
  const [startIndex, setStartIndex] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { setStartIndex(0); }, [totalItems]);
  useEffect(() => {
    if (!scrollRef) return;
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const scrollable = el.scrollHeight - el.clientHeight;
        const raw = scrollable > 0 ? Math.floor((el.scrollTop / scrollable) * totalItems) : 0;
        // Cap so at least one item remains — prevents balls from vanishing at the page bottom.
        setStartIndex(Math.min(raw, Math.max(0, totalItems - 1)));
      }, 150);
    };
    el.addEventListener('scroll', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [scrollRef, totalItems]);
  return startIndex;
}

function sampleEvenly(messages: TeamLogMessage[]): TeamLogMessage[] {
  if (messages.length === 0) return [];
  const step = Math.max(1, Math.floor(messages.length / MARKERS_TARGET));
  return messages.filter((_, i) => i % step === 0).slice(0, MARKERS_TARGET);
}

function formatMarkerTs(iso: string): { date: string; time: string } {
  try {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      time: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
    };
  } catch {
    return { date: "", time: "" };
  }
}

function previewWords(text: string, n = 8): string {
  if (!text) return "";
  const words = text.trim().split(/\s+/);
  return words.slice(0, n).join(" ") + (words.length > n ? "…" : "");
}

interface DensityDotItem {
  id: string;
  fraction: number;
  timestamp: string;
  author: string;
  content: string;
  seq: number | undefined;
}

function DensityDot({
  item,
  isActive,
  onScrollToMessage,
}: {
  item: DensityDotItem;
  isActive: boolean;
  onScrollToMessage?: (id: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ top: number; left: number } | null>(null);
  const { date, time } = formatMarkerTs(item.timestamp);

  function show() {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setTip({ top: r.top + r.height / 2, left: r.right + 12 });
  }
  function hide() { setTip(null); }

  return (
    <>
      <div
        ref={ref}
        className={`rail-density${isActive ? " active" : ""}`}
        role={onScrollToMessage ? "button" : undefined}
        tabIndex={onScrollToMessage ? 0 : undefined}
        aria-label={onScrollToMessage ? `Jump to log position ${Math.round(item.fraction * 100)}%` : undefined}
        aria-current={isActive ? "true" : undefined}
        style={{ top: `calc(5% + ${item.fraction * 90}%)` }}
        onClick={onScrollToMessage ? () => onScrollToMessage(item.id) : undefined}
        onKeyDown={onScrollToMessage ? (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onScrollToMessage(item.id);
          }
        } : undefined}
        onMouseEnter={show}
        onFocus={show}
        onMouseLeave={hide}
        onBlur={hide}
      />
      {tip && (
        <div className="marker-tip" role="tooltip" style={{ top: tip.top, left: tip.left }}>
          <div className="marker-tip-head">
            <span className="marker-tip-seq">{item.seq != null ? `#${item.seq}` : "—"}</span>
            <span className="marker-tip-ts">{date}, {time}</span>
          </div>
          <div className="marker-tip-author">{item.author}</div>
          <div className="marker-tip-preview">{previewWords(item.content)}</div>
        </div>
      )}
    </>
  );
}

// Rail 1 — teal density dots, time-proportional spacing, click-to-scroll.
// Busy periods show dots clustered together; quiet periods show dots spread apart.
export function DensityRail({
  messages,
  onScrollToMessage,
  activeFraction = -1,
  hasMoreEarlier = false,
  loadingEarlier = false,
  onLoadEarlier,
}: {
  messages: TeamLogMessage[];
  onScrollToMessage?: (id: string) => void;
  activeFraction?: number;
  hasMoreEarlier?: boolean;
  loadingEarlier?: boolean;
  onLoadEarlier?: () => void;
}) {
  // Compute raw time fractions (0–1) for each message plus message ID.
  // Dots are rendered at calc(5% + fraction * 90%) to keep them within the
  // visible rail area.  Busy periods = clustered dots; quiet = spread dots.
  const dotPositions = useMemo(() => {
    if (messages.length === 0) return [];
    const ts = messages.map(m => {
      const t = Date.parse(m.timestamp);
      return isNaN(t) ? 0 : t;
    });
    const minTs = Math.min(...ts);
    const maxTs = Math.max(...ts);
    const range = maxTs - minTs;
    return messages.map((m, i) => ({
      id: m.id,
      fraction: range > 0
        ? (ts[i] - minTs) / range
        : messages.length > 1 ? i / (messages.length - 1) : 0.5,
      timestamp: m.timestamp,
      author: typeof m.author === "string" ? m.author : String(m.author),
      content: m.content,
      seq: m.sequence,
    }));
  }, [messages]);

  // Active dot: map scroll fraction directly to array index.
  // messages are ordered oldest→newest matching top→bottom DOM order,
  // so index fraction is a much better approximation than time fraction.
  const activeIdx = activeFraction < 0 || dotPositions.length === 0
    ? -1
    : Math.round(activeFraction * (dotPositions.length - 1));

  if (dotPositions.length === 0) return null;

  const isLive = activeFraction >= 0.95;

  return (
    <div
      className="rail scroller"
      aria-label="Activity density — click to jump to time window"
      style={{ width: "var(--rail-w)", flexShrink: 0 }}
    >
      <div className="rail-line" />
      {hasMoreEarlier && onLoadEarlier && (
        <button
          type="button"
          className="rail-earlier"
          onClick={onLoadEarlier}
          disabled={loadingEarlier}
          aria-label="Load earlier messages"
          title="Load earlier messages"
        >
          <span className="chev">↑</span>
          <span className="label">{loadingEarlier ? "loading" : "earlier"}</span>
        </button>
      )}
      {dotPositions.map((item, i) => (
        <DensityDot
          key={i}
          item={item}
          isActive={i === activeIdx}
          onScrollToMessage={onScrollToMessage}
        />
      ))}
      {isLive && (
        <div className="rail-now" aria-hidden="true">
          <div className="hr" />
          <div className="pill">
            <div className="dot" />
            LIVE
          </div>
        </div>
      )}
    </div>
  );
}

// Rail 2 — orange clickable MarkerDots with hover tooltips, all same size.
export function MarkersRail({
  messages,
  onNavigate,
  activeFraction = 0,
}: {
  messages: TeamLogMessage[];
  onNavigate: (sequence: number) => void;
  /** Feed scroll position 0..1 (App's existing railScrollFraction); drives the `.on` ball. */
  activeFraction?: number;
}) {
  const markers = useMemo<Marker[]>(() => {
    const s = sampleEvenly(messages);
    const result: Marker[] = [];
    for (const m of s) {
      if (m.sequence == null) continue;
      const { date, time } = formatMarkerTs(m.timestamp);
      const author = typeof m.author === "string" ? m.author : String(m.author);
      result.push({
        seq: m.sequence,
        date,
        time,
        author,
        content: m.content.slice(0, 120),
      });
    }
    return result;
  }, [messages]);

  if (markers.length === 0) return null;

  // Ball for the entry at the top of the feed gets `.on`.
  const onIdx = activeFraction < 0 ? 0 : Math.min(markers.length - 1, Math.round(activeFraction * (markers.length - 1)));

  return (
    <div className="rail markers" aria-label="Log entries" style={{ width: "var(--rail-w)", flexShrink: 0 }}>
      <div className="rail-line" />
      {markers.map((m, i) => (
        <MarkerDot
          key={i}
          marker={m}
          on={i === onIdx}
          onClick={() => onNavigate(m.seq)}
        />
      ))}
    </div>
  );
}

// Rail 3 — orange MarkerDots for the Decisions page, same visual as Rail 2.
// Samples up to 20 decisions evenly from the current scroll position; resets on scroll.
export function DecisionsMarkersRail({ decisions, scrollRef }: { decisions: Decision[]; scrollRef?: { current: Element | null } }) {
  const startIndex = useScrollReset(scrollRef, decisions.length);
  const markers = useMemo<Array<Marker & { id: string }>>(() => {
    const slice = decisions.slice(startIndex);
    if (slice.length === 0) return [];
    const step = Math.max(1, Math.floor(slice.length / MARKERS_TARGET));
    const sampled = slice.filter((_, i) => i % step === 0).slice(0, MARKERS_TARGET);
    return sampled.map(d => {
      const dt = new Date(d.createdAt);
      const date = dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      const time = dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
      const words = d.question.trim().split(/\s+/);
      const content = words.slice(0, 8).join(" ") + (words.length > 8 ? "…" : "");
      return {
        id: d.id,
        seq: d.sequence,
        date,
        time,
        author: d.category,
        content,
      };
    });
  }, [decisions, startIndex]);

  if (markers.length === 0) return null;

  return (
    <div className="rail markers" aria-label="Decisions navigation" style={{ width: "var(--rail-w)", flexShrink: 0, gridColumn: 3, gridRow: 2 }}>
      <div className="rail-line" />
      {markers.map((m, i) => (
        <MarkerDot
          key={i}
          on={i === 0}
          marker={m}
          onClick={() => {
            const el = document.getElementById(`decision-${m.id}`);
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
      ))}
    </div>
  );
}

// Shared section-rail renderer: MarkerDot per section, scroll-reset via useScrollReset.
// Shows sections from current scroll position onward (max 10).
function SectionMarkersRail({
  sections,
  ariaLabel,
  scrollRef,
}: {
  sections: { id: string; label: string; desc: string }[];
  ariaLabel: string;
  scrollRef?: { current: Element | null };
}) {
  const startIndex = useScrollReset(scrollRef, sections.length);
  const visible = sections.slice(startIndex);
  return (
    <div
      className="rail markers"
      aria-label={ariaLabel}
      style={{ width: "var(--rail-w)", flexShrink: 0, gridColumn: 3, gridRow: 2 }}
    >
      <div className="rail-line" />
      {visible.map((s, i) => (
        <MarkerDot
          key={s.id}
          on={i === 0}
          marker={{ seq: startIndex + i + 1, date: s.label, time: "", author: "", content: s.desc }}
          onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
        />
      ))}
    </div>
  );
}

// Rail 6 — Team page section navigation (4 sections, scroll-reset).
// Workspace Governance moved to its own Control Center tab (Charter |
// Workspace Governance) — no longer part of the Team page.
// track:track-backlog--standardize-branding-across-all-tabs — added the
// missing team-section-mcp entry (McpConnectionSection, rendered first in
// TeamPage.tsx) to this list; the other 4 ids were already correctly wired,
// this rail just never covered all 5 of the page's real sections.
const TEAM_SECTIONS = [
  { id: "team-section-mcp",        label: "Connect your AI",      desc: "Relay Helper key and MCP connections" },
  { id: "team-section-human",      label: "Human Partners",       desc: "Members, roles, and invite" },
  { id: "team-section-invite",     label: "Invite Link",          desc: "Workspace join code and link" },
  { id: "team-section-ai",         label: "AI Partners",          desc: "Relay, Advisor, Developers" },
  { id: "team-section-system",     label: "System Team",          desc: "Daemon and Scheduler" },
];

export function TeamPageMarkersRail({ scrollRef }: { scrollRef?: { current: Element | null } }) {
  return <SectionMarkersRail sections={TEAM_SECTIONS} ariaLabel="Team page navigation" scrollRef={scrollRef} />;
}

// Rail 7 — Charter page section navigation (4 sections, scroll-reset).
const CHARTER_SECTIONS = [
  { id: "charter-section-intro",     label: "How It Works",    desc: "Platform, workspace, and custom rule overview" },
  { id: "charter-section-platform",  label: "Platform Rules",  desc: "Always-on rules set by easyrelay.app" },
  { id: "charter-section-workspace", label: "Workspace Rules", desc: "Configurable toggles for your team" },
  { id: "charter-section-custom",    label: "Custom Rules",    desc: "Plain-language rules you define" },
];

export function CharterMarkersRail({ scrollRef }: { scrollRef?: { current: Element | null } }) {
  return <SectionMarkersRail sections={CHARTER_SECTIONS} ariaLabel="Charter page navigation" scrollRef={scrollRef} />;
}

// track:skills-retirement-relay (decision #49035, spec #49036 Slice 2) —
// Rail 10 (SkillsMarkersRail / SKILLS_SECTIONS) removed along with the
// retired Skills tab. Easy Relay Skill's own section moved to TeamPage.tsx,
// which has no per-section rail of its own.

// Rail 12 — Workspace Governance page section navigation (5 sections,
// scroll-reset). track:track-backlog--standardize-branding-and-nav-rail-
// across-workspace-tabs — this tab (part of the Charter | Workspace
// Governance | Project Management group) never had a rail at all before
// this track.
const GOVERNANCE_SECTIONS = [
  { id: "governance-section-autonomy",      label: "Agent Autonomy",      desc: "How much agents can do without approval" },
  { id: "governance-section-approval",      label: "Approval Threshold",  desc: "Which blast-radius levels auto-approve" },
  { id: "governance-section-narration",     label: "@relay Narration",    desc: "Whether @relay posts narration to the Team Log" },
  { id: "governance-section-planning",      label: "Planning Mode",       desc: "@relay-led or advisor-led sprint planning" },
  { id: "governance-section-communication", label: "Communication Style", desc: "@relay's language register" },
];

export function WorkspaceGovernanceMarkersRail({ scrollRef }: { scrollRef?: { current: Element | null } }) {
  return <SectionMarkersRail sections={GOVERNANCE_SECTIONS} ariaLabel="Workspace Governance page navigation" scrollRef={scrollRef} />;
}

// Rail 13 — Project Management page section navigation. Just one section
// today (Project Visibility, moved here from Skills) — room to grow as
// more project-level settings land on this tab.
const PROJECT_MANAGEMENT_SECTIONS = [
  { id: "project-mgmt-section-visibility", label: "Project Visibility", desc: "Show or hide projects in the sidebar" },
];

export function ProjectManagementMarkersRail({ scrollRef }: { scrollRef?: { current: Element | null } }) {
  return <SectionMarkersRail sections={PROJECT_MANAGEMENT_SECTIONS} ariaLabel="Project Management page navigation" scrollRef={scrollRef} />;
}

// Rail 14 — Test-A-Feature "All Projects" page navigation. Unlike every
// other SectionMarkersRail consumer above, this rail's sections are
// dynamic (one per currently-visible project, computed by
// FeatureTestingPage.tsx and reported up via its onSectionsChange prop)
// rather than a fixed module-level list — so this wrapper takes
// `sections` as a parameter instead of hardcoding one. App.tsx only
// renders this component when sections.length > 0 (i.e. All Projects
// mode is active), so there's no separate empty-state to handle here.
export function TestAFeatureMarkersRail({
  sections,
  scrollRef,
}: {
  sections: { id: string; label: string; desc: string }[];
  scrollRef?: { current: Element | null };
}) {
  return <SectionMarkersRail sections={sections} ariaLabel="Test-A-Feature navigation" scrollRef={scrollRef} />;
}

// Rail 15 — Create-A-Project (formerly "Project Background") "All Projects"
// page navigation. Same dynamic-sections shape as Rail 14 above (one marker
// per currently-visible project, computed by ProjectBackgroundPage.tsx and
// reported up via its onSectionsChange prop) — App.tsx only renders this
// component when sections.length > 0 (i.e. "All Projects" is selected in
// the page's dropdown), so there's no separate empty-state to handle here.
export function ProjectBackgroundMarkersRail({
  sections,
  scrollRef,
}: {
  sections: { id: string; label: string; desc: string }[];
  scrollRef?: { current: Element | null };
}) {
  return <SectionMarkersRail sections={sections} ariaLabel="Create-A-Project navigation" scrollRef={scrollRef} />;
}

// Rail 11 — Account page section navigation (2 sections, scroll-reset).
// track:track-backlog--move-profile-into-workspace-tab-group-as-account
// — folded the standalone Profile page into the Charter | Workspace
// Governance | Project Management tab group (now Account leads it) per
// Travis. Also fixes the same dead-rail bug: the
// old 3 section ids (profile-section-user/workspace/projects, "User /
// Workspace / Projects") didn't exist anywhere in ProfilePage.tsx's DOM —
// "Projects" in particular described Project Visibility, which had
// already moved out to its own Project Management tab. Corrected to the
// page's real 2 sections, renamed User -> Account and Workspace ->
// Subscription (that section is about the user's subscription plan, not
// "workspace" settings — doubly confusing inside the Workspace tab group).
const ACCOUNT_SECTIONS = [
  { id: "account-section-account",      label: "Account",      desc: "Name, email, and sign-out" },
  { id: "account-section-subscription", label: "Subscription", desc: "Plan and @relay usage" },
];

export function AccountMarkersRail({ scrollRef }: { scrollRef?: { current: Element | null } }) {
  return <SectionMarkersRail sections={ACCOUNT_SECTIONS} ariaLabel="Account page navigation" scrollRef={scrollRef} />;
}

// Rail 8 — orange MarkerDots for the Backlog column inside Control Center.
// Resamples 20 items from the current scroll position on debounced scroll ("reset" behavior).
interface BacklogMarkerItem {
  id: string;
  title: string;
  sequence: number;
  tier: 'now' | 'soon' | 'later';
}

export function BacklogMarkersRail({
  items,
  scrollRef,
  maxMarkers = 20,
  side = 'right',
}: {
  items: BacklogMarkerItem[];
  scrollRef: React.RefObject<HTMLDivElement | null>;
  maxMarkers?: number;
  side?: 'left' | 'right';
}) {
  const startIndex = useScrollReset(scrollRef, items.length);

  const markers = useMemo(() => {
    const slice = items.slice(startIndex);
    if (slice.length === 0) return [];
    const step = Math.max(1, Math.floor(slice.length / maxMarkers));
    return slice.filter((_, i) => i % step === 0).slice(0, maxMarkers);
  }, [items, startIndex, maxMarkers]);

  if (items.length < 2) return null;

  const border = side === 'left'
    ? { borderRight: '1px solid rgba(95,196,192,0.12)' }
    : { borderLeft: '1px solid rgba(95,196,192,0.12)' };

  return (
    <div
      style={{
        width: 14,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-evenly',
        padding: '6px 0',
        position: 'relative',
        ...border,
      }}
      aria-label="Backlog navigation"
    >
      <div style={{
        position: 'absolute',
        left: '50%',
        top: '5%',
        bottom: '5%',
        width: 1,
        transform: 'translateX(-50%)',
        background: 'var(--orange, #F97316)',
        opacity: 0.18,
        pointerEvents: 'none',
      }} />
      {markers.map((item, i) => (
        <MarkerDot
          key={`${item.id}-${i}`}
          on={i === 0}
          marker={{
            seq: item.sequence,
            date: item.tier.toUpperCase(),
            time: '',
            author: '',
            content: item.title,
          }}
          onClick={() => {
            document.getElementById(`backlog-item-${item.id}`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }}
        />
      ))}
    </div>
  );
}

// Rail 9 — orange MarkerDots for the full BacklogPage (gridColumn 3, same as Decisions).
// Samples up to 20 items evenly; clicking scrolls the backlog card into view in the main feed.
export function BacklogPageMarkersRail({ items }: { items: import('../components/BacklogPage').BacklogItem[] }) {
  const markers = useMemo(() => {
    if (items.length === 0) return [];
    const step = Math.max(1, Math.floor(items.length / MARKERS_TARGET));
    return items.filter((_, i) => i % step === 0).slice(0, MARKERS_TARGET);
  }, [items]);

  if (markers.length === 0) return null;

  return (
    <div
      className="rail markers"
      aria-label="Backlog navigation"
      style={{ width: 'var(--rail-w)', flexShrink: 0, gridColumn: 3, gridRow: 2 }}
    >
      <div className="rail-line" />
      {markers.map((item, i) => (
        <MarkerDot
          key={i}
          on={i === 0}
          marker={{
            seq: item.sequence,
            date: '',
            time: '',
            author: item.author,
            content: item.content.replace(/[#*`>_]/g, '').slice(0, 80),
          }}
          onClick={() => {
            document.getElementById(`backlog-page-item-${item.id}`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      ))}
    </div>
  );
}

// Rail 5 — orange MarkerDots for the Help page.
// Samples up to 20 help-tagged questions from the current scroll position; resets on scroll.
export function HelpMarkersRail({ messages, scrollRef }: { messages: HelpMessage[]; scrollRef?: { current: Element | null } }) {
  const questions = useMemo(() => messages.filter(m => m.tags.includes("relay-help")), [messages]);
  const startIndex = useScrollReset(scrollRef, questions.length);
  const markers = useMemo<Array<Marker & { seq: number }>>(() => {
    const slice = questions.slice(startIndex);
    if (slice.length === 0) return [];
    const step = Math.max(1, Math.floor(slice.length / MARKERS_TARGET));
    const sampled = slice.filter((_, i) => i % step === 0).slice(0, MARKERS_TARGET);
    return sampled.map(q => {
      const { date, time } = formatMarkerTs(q.timestamp);
      const words = q.content.replace(/[#*`>_]/g, "").trim().split(/\s+/);
      const content = words.slice(0, 8).join(" ") + (words.length > 8 ? "…" : "");
      return { id: String(q.sequence), seq: q.sequence, date, time, author: q.author, content };
    });
  }, [questions, startIndex]);

  if (markers.length === 0) return null;

  return (
    <div className="rail markers" aria-label="Help navigation" style={{ width: "var(--rail-w)", flexShrink: 0, gridColumn: 3, gridRow: 2 }}>
      <div className="rail-line" />
      {markers.map((m, i) => (
        <MarkerDot
          key={i}
          on={i === 0}
          marker={m}
          onClick={() => {
            const el = document.getElementById(`help-thread-${m.seq}`);
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
      ))}
    </div>
  );
}
